import { AuthBaseClient } from '../base-client';
import type { UserPreferencesResponse } from './types';

/**
 * Typed client for the current user's UI preferences.
 *
 * Hydrate once on app load via `get()`, cache in TanStack Query. Save
 * per-view via `upsertView()` — the server replaces the payload at
 * `viewKey`, preserves sibling views, and returns the full post-write
 * blob so the cache can be overwritten without a follow-up GET.
 */
export class UserPreferencesClient extends AuthBaseClient {
  /** Fetch the full preferences blob for the signed-in user. */
  async getPreferences(): Promise<UserPreferencesResponse> {
    return this.get<UserPreferencesResponse>('/api/user-preferences');
  }

  /**
   * Replace the per-view payload stored at `viewKey`. Atomic at the DB
   * level — only the one slot inside the JSONB blob changes, siblings
   * are untouched. Two tabs writing DIFFERENT view_keys are race-safe;
   * two tabs writing the SAME view_key are last-write-wins.
   */
  async upsertView(
    viewKey: string,
    payload: Record<string, unknown>
  ): Promise<UserPreferencesResponse> {
    return this.patch<UserPreferencesResponse>(
      `/api/user-preferences/${encodeURIComponent(viewKey)}`,
      payload
    );
  }

  /**
   * Remove the entry stored at `viewKey` entirely (key is dropped from
   * the JSONB blob). Use when a user resets a view to defaults — PATCH
   * can only blank a view's payload to `{}`; this fully removes it so
   * the next session's hydrate won't redirect to a saved preference.
   */
  async deleteView(viewKey: string): Promise<UserPreferencesResponse> {
    return this.delete<UserPreferencesResponse>(
      `/api/user-preferences/${encodeURIComponent(viewKey)}`
    );
  }
}
